import { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    SxProps,
    Theme,
    TextField,
    Checkbox,
    FormControlLabel,
    TypographyProps,
    Link,
    Stack,
} from "@mui/material";
import { useContact } from "../hooks";
import { isValidMail } from "../utils/inputUtils";
import { CommonStyles } from "../styles/CommonStyles";
import ReinovaButton from "../components/ReinovaButton";
import PageTitle from "../components/PageTitle";
import PageContent from "../components/PageContent";
import { useIsMobile } from "../hooks";
import { useTranslation } from "react-i18next";

const ContactPageStyle: Record<string, SxProps<Theme>> = {
    captionTitle: {
        fontSize: "27px",
        textAlign: "left",
        marginBottom: "20px",
    },
    captionText: {
        fontSize: "22px",
        textAlign: "left",
    },
    formContainer: {
        marginTop: "20px",
    },
    formColumn: {
        display: "flex",
        flexDirection: "column",
    },
    formFooter: {
        marginTop: "20px",
        alignItems: "center",
    },
    formFooterConditions: {
        fontSize: "0.9rem",
        color: "#bababa",
        textAlign: "left",
    },
    formItem: {
        marginBottom: "10px",
        marginTop: "10px",
        textAlign: "left",
        minWidth: "70%",
    },
    formSuccessText: {
        color: "#81BC00",
    },
    formErrorText: {
        color: "red",
    },
};

const ContactPage = () => {
    const { isMobile } = useIsMobile();
    const { postContactForm } = useContact();
    const { t } = useTranslation();

    const [formName, setFormName] = useState("");
    const [formEmail, setFormEmail] = useState("");
    const [formMessage, setFormMessage] = useState("");
    const [formCondition1, setFormCondition1] = useState(false);
    const [formResult, setFormResult] = useState<boolean | null>(null);

    const isValid = () => {
        return (
            formName !== "" &&
            isValidMail(formEmail) &&
            formMessage !== "" &&
            formCondition1 
        );
    };

    const resetForm = () => {
        setFormName("");
        setFormMessage("");
        setFormEmail("");
        setFormCondition1(false);
    };

    const handleSubmit = () => {
        postContactForm({ name: formName, email: formEmail, message: formMessage })
            .then((res) => {
                setFormResult(true);
                resetForm();
            })
            .catch((e) => setFormResult(false));
    };

    return (
        <>
            <PageTitle>{t("menu.contact")}</PageTitle>
            <PageContent>
                <Grid item md={12}>
                    <Typography
                        sx={
                            isMobile
                                ? CommonStyles.pageSubtitleMobile
                                : CommonStyles.pageSubtitle
                        }
                    >
                        {t("contact.1")}
                    </Typography>

                    <Typography
                        sx={
                            isMobile
                                ? CommonStyles.pageSubtitleMobile
                                : CommonStyles.pageSubtitle
                        }
                    >
                        {t("contact.2")}
                    </Typography>

                    <Typography
                        sx={
                            isMobile
                                ? CommonStyles.pageSubtitleMobile
                                : CommonStyles.pageSubtitle
                        }
                    >
                        {t("contact.3")}
                    </Typography>
                    
                    <Stack direction={"row"} justifyContent={"center"}>
                        <Typography
                            sx={
                                isMobile
                                    ? CommonStyles.pageSubtitleMobile
                                    : CommonStyles.pageSubtitle
                            }
                        >
                            {t("contact.4")}
                            <a href={"mailto:segnalazioni@reinova.tech"}>segnalazioni@reinova.tech</a>
                        </Typography>
                    </Stack>
                </Grid>

                <Grid
                    item
                    md={12}
                    container
                    sx={ContactPageStyle.formContainer}
                    spacing={isMobile ? 0 : 6}
                >
                    <Grid item lg={2} md={0} sm={0} xs={0} />
                    <Grid
                        item
                        lg={4}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={ContactPageStyle.formColumn}
                    >
                        <Box sx={[ContactPageStyle.formItem, isMobile ? {} : {}].flat(2)}>
                            <Typography sx={CommonStyles.formInputLabel}>{t("contact.name")}</Typography>
                            <TextField
                                value={formName}
                                inputProps={{
                                    style: {
                                        color: "white",
                                    },
                                }}
                                sx={[CommonStyles.formInput, { width: "100%" }].flat(2)}
                                size="small"
                                required
                                color="secondary"
                                onChange={(e) => setFormName(e.target.value)}
                            />
                        </Box>
                        <Box sx={[ContactPageStyle.formItem, isMobile ? {} : {}].flat(2)}>
                            <Typography sx={CommonStyles.formInputLabel}>
                                {t("contact.message")}
                            </Typography>
                            <TextField
                                value={formMessage}
                                inputProps={{
                                    style: {
                                        color: "white",
                                    },
                                }}
                                rows={10}
                                multiline
                                sx={[CommonStyles.formInput, { width: "100%" }].flat(2)}
                                size="small"
                                required
                                color="secondary"
                                onChange={(e) => setFormMessage(e.target.value)}
                            />
                        </Box>
                    </Grid>
                    <Grid item lg={4} md={12} sx={[ContactPageStyle.formColumn].flat(2)}>
                        <Box sx={ContactPageStyle.formItem}>
                            <Typography sx={CommonStyles.formInputLabel}>Email*</Typography>
                            <TextField
                                value={formEmail}
                                inputProps={{
                                    style: {
                                        color: "white",
                                    },
                                }}
                                sx={[CommonStyles.formInput, { width: "100%" }].flat(2)}
                                size="small"
                                required
                                color="secondary"
                                //error={!isValidMail(formEmail)}
                                onChange={(e) => setFormEmail(e.target.value)}
                            />
                        </Box>
                        <Box sx={ContactPageStyle.formFooter}>
                            <Box sx={ContactPageStyle.formItem}>

                                <FormControlLabel
                                    checked={formCondition1}
                                    onChange={(e, checked) => setFormCondition1(checked)}
                                    componentsProps={{
                                        typography:
                                            ContactPageStyle.formFooterConditions as TypographyProps,
                                    }}
                                    required
                                    control={
                                        <Checkbox sx={{ color: "#bababa" }} color="secondary" />
                                    }
                                    label={<>{t("contact.privacy")}
                                        <Link href={"/privacy"} color={"inherit"}>
                                            privacy policy
                                        </Link>
                                    </>}
                                />
                            </Box>
                            <Box
                                sx={{
                                    textAlign: isMobile ? "center" : "left",
                                    marginTop: isMobile ? "20px" : "0px",
                                }}
                            >
                                <ReinovaButton
                                    disabled={!isValid()}
                                    onClick={() => {
                                        handleSubmit();
                                    }}
                                    style={{ alignSelf: "center" }}
                                >
                                    {t("contact.send")}
                                </ReinovaButton>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item lg={2} md={0} />
                    {formResult !== null && (
                        <Grid item md={12} sx={ContactPageStyle.formColumn}>
                            {formResult && (
                                <Typography sx={ContactPageStyle.formSuccessText}>
                                    {t("contact.sendok")}
                                </Typography>
                            )}
                            {!formResult && (
                                <Typography sx={ContactPageStyle.formErrorText}>
                                    {t("contact.sendko")}
                                </Typography>
                            )}
                        </Grid>
                    )}
                </Grid>
            </PageContent>
        </>
    );
};

export default ContactPage;
